<template>
    <div>
        <Card
            :title="user.nickname || user.email"
            read="Edit"
            @read="load()"
            write="Save"
            @write="save()"
            remove="Remove"
            @remove="remove()"
            :border-variant="user.connected ? 'success' : 'gray'"
        >
            <template v-slot:header-show>
                <b-form-checkbox
                    v-model="mutatedUser.enabled"
                    switch
                    size="lg"
                    class="d-inline"
                />
            </template>
            <template v-slot:header-hide>
                <b-form-checkbox
                    v-model="user.enabled"
                    switch
                    disabled
                    size="lg"
                    class="d-inline"
                />
            </template>
            <template v-slot:header>
                <b-badge pill class="mr-2" :variant="roleVariant">
                    {{user.accessLevel}}
                </b-badge>
            </template>
            <Input text="Upload" :value="user.upload | formatData" disabled />
            <Input
                text="Download"
                :value="user.download | formatData"
                disabled
            />
            <Input
                text="Time used"
                :value="user.timeUsed | formatMinute"
                disabled
            />
            <Input
                text="Last connected"
                :value="(user.latestHandshake * 1000) | formatDate"
                :append="(user.latestHandshake * 1000) | formatTimeDistance"
                disabled
            />
            <Input text="Endpoint" :value="user.endpoint || 'None'" disabled />
            <hr />
            <b-input-group prepend="Plan" size="sm" class="mb-1">
                <b-form-select v-model="mutatedUser.planName">
                    <b-form-select-option
                        v-for="plan in plans"
                        :key="plan._id"
                        :value="plan.name"
                    >
                        {{ plan.nickname || plan.name }}
                    </b-form-select-option>
                </b-form-select>
            </b-input-group>

            <Input
                text="Balance"
                v-model="mutatedUser.balance"
                :append="mutatedUser.balance | formatCurrency"
                type="number"
            />
            <Input
                text="Credit"
                v-model="mutatedUser.credit"
                :append="mutatedUser.credit | formatCurrency"
                type="number"
            />
            <div v-if="user.plan">
                <Input
                    v-if="user.plan.timeCap"
                    text="Time cap"
                    :value="user.plan.timeCap | formatMinute"
                    :append="
                        (user.plan.timeCap - mutatedUser.timeUsed)
                            | formatMinute
                    "
                    disabled
                />
                <Input
                    v-if="user.plan.dataCap"
                    text="Data cap"
                    :value="user.plan.dataCap | formatData"
                    :append="
                        (user.plan.dataCap -
                            mutatedUser.upload -
                            mutatedUser.download)
                            | formatData
                    "
                    disabled
                />
                <Deposit :id="user._id" />
                <Input
                    text="Bill"
                    :value="user.bill | formatCurrency"
                    disabled
                />
                <Input
                    text="Static bill"
                    v-model="mutatedUser.staticBill"
                    :append="mutatedUser.staticBill | formatCurrency"
                />
                <Input
                    v-if="user.plan.billingAmountPerGB"
                    text="Data bill"
                    :value="user.dataBill | formatCurrency"
                    disabled
                />
                <Input
                    v-if="user.plan.billingAmountPerHour"
                    text="Time bill"
                    :value="user.timeBill | formatCurrency"
                    disabled
                />
                <Input
                    text="Last billed"
                    :value="user.lastBilled | formatDate"
                    :append="user.lastBilled | formatTimeDistance"
                    disabled
                />
                <Input
                    text="Next billing date"
                    :value="
                        (parseInt(user.lastBilled) +
                            parseInt(user.plan.billingInterval) * 60000)
                            | formatDate
                    "
                    :append="
                        (parseInt(user.lastBilled) +
                            parseInt(user.plan.billingInterval) * 60000)
                            | formatTimeDistance
                    "
                    disabled
                />
            </div>
            <b-button
                block
                size="sm"
                variant="light"
                @click="
                    $store
                        .dispatch('admin/users/settleBill', user._id)
                        .then(() => {
                            $bvToast.toast('Bill settled.', {
                                title: 'Success',
                                variant: 'success'
                            })
                        })
                        .catch(error => {
                            $bvToast.toast(error, {
                                title: 'Error',
                                variant: 'warning'
                            })
                        })
                "
            >
                Force reset billing period
            </b-button>
            <hr />
            <Input text="Email" :value="user.email" disabled />
            <Input text="Nickname" v-model="mutatedUser.nickname" />
            <Input
                text="Access level"
                v-model="mutatedUser.accessLevel"
                :append="mutatedUser.accessLevel | formatAccessLevel"
                type="number"
            />
            <Input
                text="Activation code"
                v-model="mutatedUser.activationCode"
                :append="
                    mutatedUser.activationCode ? 'Deactivated' : 'Activated'
                "
            />
            <Input text="Recovery code" v-model="mutatedUser.recoveryCode" />
            <Input text="Password" v-model="mutatedUser.password" />
            <Input
                text="Last logged in"
                v-if="user.lastLogin"
                :value="user.lastLogin | formatDate"
                :append="user.lastLogin | formatTimeDistance"
                disabled
            />
            <Input
                text="2nd last logged in"
                v-if="user.last2ndLogin"
                :value="user.last2ndLogin | formatDate"
                :append="user.last2ndLogin | formatTimeDistance"
                disabled
            />
            <Input
                text="Last authenticated"
                v-if="user.lastAuthenticated"
                :value="user.lastAuthenticated | formatDate"
                :append="user.lastAuthenticated | formatTimeDistance"
                disabled
            />
            <Input
                text="Registered on"
                :value="user.registrationDate | formatDate"
                :append="user.registrationDate | formatTimeDistance"
                disabled
            />
            <hr />
            <DeviceList :userName="user.email" />
        </Card>
    </div>
</template>

<script>
    import { mapState } from 'vuex'

    import Input from '@/components/form/input'
    import DeviceList from '@/components/admin/devices/list'

    import Card from '@/components/general/card'

    import Deposit from './deposit'

    export default {
        props: ['id'],
        data() {
            return {
                mutatedUser: {}
            }
        },
        methods: {
            load() {
                return this.$store
                    .dispatch('admin/users/loadByID', this.user._id)
                    .then(() => {
                        this.mutatedUser = { ...this.user }
                    })
            },
            save() {
                return this.$store
                    .dispatch('admin/users/save', this.mutatedUser)
                    .then(() => {
                        this.$bvToast.toast('User saved.', {
                            title: 'Success',
                            variant: 'success'
                        })
                    })
            },
            remove() {
                return this.$store
                    .dispatch('admin/users/remove', this.user._id)
                    .then(() => {
                        this.$bvToast.toast('User removed.', {
                            title: 'Success',
                            variant: 'success'
                        })
                    })
            }
        },
        computed: {
            user() {
                return this.$store.getters['admin/users/getByID'](this.id)
            },
            ...mapState({
                plans: state => state.admin.plans.plans
            }),
            roleVariant() {
                return [null, 'dark', 'primary', null, null, 'warning', 'danger'][
                    this.user.accessLevel
                ]
            }
        },
        components: {
            Input,
            DeviceList,
            Card,
            Deposit
        }
    }
</script>